import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, mediaUrl } from '@/lib/api';
import { useGlobalAudio } from '@/components/GlobalAudioPlayer';
import { Play, Pause, Music as MusicIcon, Mic2, Newspaper, ShoppingBag, Video as VideoIcon, Image as ImageIcon, ExternalLink } from 'lucide-react';

/**
 * HomepageCollections
 * -------------------
 * Renders the admin-defined collections from /api/homepage/collections. When
 * no collections are configured, renders nothing (the default homepage
 * sections continue to work unchanged).
 *
 * Each collection can be a grid, horizontal rail, or list. Music/podcast items
 * expose a Play Now button wired to the global audio player.
 */
export default function HomepageCollections({ source = 'live', onLoaded }) {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/homepage/collections?src=${source === 'draft' ? 'draft' : 'live'}`);
        const cols = (data && data.collections) || [];
        if (!cancelled) {
          setCollections(cols);
          const visibleCount = cols.filter((c) => (c.items || []).length > 0).length;
          if (onLoaded) onLoaded(visibleCount);
        }
      } catch (_) {
        if (!cancelled) {
          setCollections([]);
          if (onLoaded) onLoaded(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [source, onLoaded]);

  if (loading || collections.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 space-y-10" data-testid="homepage-collections">
      {collections.map((c) => (
        <CollectionBlock key={c.id} coll={c} />
      ))}
    </section>
  );
}

function CollectionBlock({ coll }) {
  const items = coll.items || [];
  if (items.length === 0) return null;
  const style = coll.style || 'grid';
  return (
    <div data-testid={`collection-block-${coll.id}`}>
      <div className="flex items-end justify-between gap-3 mb-4">
        <div>
          {coll.kicker && <p className="pg-label text-[10px]">{coll.kicker}</p>}
          <h2 className="font-display text-2xl sm:text-3xl mt-1 tracking-[0.02em] text-[var(--pg-ink)]">{coll.title}</h2>
          <p className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] mt-1 uppercase">
            {coll.display_mode} · {coll.content_source} · {items.length} items
          </p>
        </div>
      </div>
      {style === 'rail' ? (
        <div className="flex gap-4 overflow-x-auto pg-scroll pb-2 snap-x snap-mandatory">
          {items.map((it) => (
            <div key={`${it.__type}:${it.id}`} className="snap-start shrink-0 w-56">
              <ItemCard item={it} />
            </div>
          ))}
        </div>
      ) : style === 'list' ? (
        <ul className="pg-card overflow-hidden divide-y divide-[var(--pg-border-2)]">
          {items.map((it) => (
            <li key={`${it.__type}:${it.id}`} className="p-3">
              <ItemRow item={it} />
            </li>
          ))}
        </ul>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {items.map((it) => (
            <ItemCard key={`${it.__type}:${it.id}`} item={it} />
          ))}
        </div>
      )}
    </div>
  );
}

function typeIcon(t) {
  switch (t) {
    case 'music': return MusicIcon;
    case 'podcast': return Mic2;
    case 'article': return Newspaper;
    case 'mobile_read': return Newspaper;
    case 'product': return ShoppingBag;
    case 'artwork': return ImageIcon;
    case 'video': return VideoIcon;
    default: return ImageIcon;
  }
}

function ItemCard({ item }) {
  const t = item.__type;
  const Icon = typeIcon(t);
  const img = item.primary_image_url || item.cover_url || item.image_url || item.poster_url;
  const href = itemHref(item);
  const audioPlayable = (t === 'music' || t === 'podcast') && (item.file_url || (item.source_type === 'embed' && item.embed_url));
  const { current, isPlaying, togglePlay, playTrack } = useGlobalAudio();
  const active = audioPlayable && current?.id === item.id;
  const showPlay = !active || !isPlaying;
  const play = () => (active ? togglePlay() : playTrack(item));

  return (
    <div className="pg-card overflow-hidden group" data-testid="collection-item">
      <div className="relative aspect-square bg-[var(--pg-navy-800)]">
        {img ? <img src={mediaUrl(img)} alt={item.title || ''} className="w-full h-full object-cover" /> : (
          <div className="w-full h-full flex items-center justify-center text-[var(--pg-emerald)]"><Icon size={40} /></div>
        )}
        {audioPlayable && (
          <button
            onClick={play}
            aria-label={showPlay ? `Play ${item.title}` : `Pause ${item.title}`}
            className={`absolute inset-0 flex items-center justify-center transition-opacity ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
            style={{ background: 'rgba(4,6,12,0.4)' }}
            data-testid="collection-item-play"
          >
            <span className="h-12 w-12 rounded-full bg-[var(--pg-emerald)] text-[var(--pg-ink)] flex items-center justify-center">
              {showPlay ? <Play size={16} className="ml-0.5" /> : <Pause size={16} />}
            </span>
          </button>
        )}
      </div>
      {href ? (
        href.startsWith('http') ? (
          <a href={href} target="_blank" rel="noreferrer" className="block p-3">
            <ItemMeta item={item} />
            <span className="text-[10px] font-mono text-[var(--pg-ink-faint)] mt-1 inline-flex items-center gap-1"><ExternalLink size={10} /> EXTERNAL</span>
          </a>
        ) : (
          <Link to={href} className="block p-3">
            <ItemMeta item={item} />
          </Link>
        )
      ) : (
        <div className="p-3"><ItemMeta item={item} /></div>
      )}
    </div>
  );
}

function ItemRow({ item }) {
  const t = item.__type;
  const Icon = typeIcon(t);
  const img = item.primary_image_url || item.cover_url || item.image_url || item.poster_url;
  const audioPlayable = (t === 'music' || t === 'podcast') && (item.file_url || (item.source_type === 'embed' && item.embed_url));
  const { current, isPlaying, togglePlay, playTrack } = useGlobalAudio();
  const active = audioPlayable && current?.id === item.id;
  const showPlay = !active || !isPlaying;
  const play = () => (active ? togglePlay() : playTrack(item));
  const href = itemHref(item);
  return (
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-md overflow-hidden bg-[var(--pg-navy-800)] shrink-0 border border-[var(--pg-border-2)]">
        {img ? <img src={mediaUrl(img)} alt="" className="w-full h-full object-cover" /> : (
          <div className="w-full h-full flex items-center justify-center text-[var(--pg-emerald)]"><Icon size={14} /></div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        {href
          ? (href.startsWith('http')
              ? <a href={href} target="_blank" rel="noreferrer" className="font-serif text-sm text-[var(--pg-ink)] truncate hover:pg-text-glow-emerald">{item.title}</a>
              : <Link to={href} className="font-serif text-sm text-[var(--pg-ink)] truncate hover:pg-text-glow-emerald">{item.title}</Link>)
          : <span className="font-serif text-sm text-[var(--pg-ink)] truncate">{item.title}</span>}
        <p className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase">{t}</p>
      </div>
      {audioPlayable && (
        <button onClick={play} className="h-8 w-8 rounded-full bg-[var(--pg-emerald)] text-[var(--pg-ink)] flex items-center justify-center" aria-label={showPlay ? `Play ${item.title}` : `Pause ${item.title}`}>
          {showPlay ? <Play size={12} className="ml-0.5" /> : <Pause size={12} />}
        </button>
      )}
    </div>
  );
}

function ItemMeta({ item }) {
  return (
    <>
      <p className="font-serif text-sm text-[var(--pg-ink)] line-clamp-2 leading-snug">{item.title || 'Untitled'}</p>
      <p className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase mt-1 truncate">
        {item.__type}
        {item.price ? ` · $${item.price} ${(item.currency || 'usd').toUpperCase()}` : ''}
      </p>
    </>
  );
}

function itemHref(item) {
  const t = item.__type;
  switch (t) {
    case 'music': return '/music';
    case 'podcast': return '/podcasts';
    case 'article': return item.slug ? `/on-my-mind/${item.slug}` : '/on-my-mind';
    case 'mobile_read': return '/mobile-reads';
    case 'product': return `/vault/${item.id}`;
    case 'artwork': return '/collect';
    case 'video': return item.embed_url || '/watch';
    default: return null;
  }
}
